import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getInstallmentAnalyticsThunk } from "../../../store/thunks/branchInstallmentThunks";

import {
  selectInstallmentAnalytics,
  selectIsLoadingAnalytics,
  selectDefaultRate,
  selectTotalRevenue,
} from "../../../store/selectors/branchInstallmentSelectors";
import {
  ChartBarIcon,
  TrendingUpIcon,
  TrendingDownIcon,
} from "@heroicons/react/outline";

const InstallmentAnalytics = ({ branchCode }) => {
  const dispatch = useDispatch();
  const analytics = useSelector(selectInstallmentAnalytics);
  const isLoading = useSelector(selectIsLoadingAnalytics);
  const defaultRate = useSelector(selectDefaultRate);
  const totalRevenue = useSelector(selectTotalRevenue);

  const [dateRange, setDateRange] = useState({
    fromDate: "",
    toDate: "",
  });

  useEffect(() => {
    loadAnalytics();
  }, [branchCode]);

  const loadAnalytics = () => {
    dispatch(
      getInstallmentAnalyticsThunk({
        branchCode,
        ...dateRange,
      })
    );
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilter = () => {
    loadAnalytics();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <ChartBarIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
        <p className="text-gray-600">No analytics data available</p>
      </div>
    );
  }

  const overview = analytics.overview || {};
  const byStatus = analytics.byStatus || [];
  const byPlan = analytics.byPlan || [];
  const paymentPerformance = analytics.paymentPerformance || {};

  return (
    <div className="space-y-6">
      {/* Date Range Filter */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-semibold mb-3">Date Range Filter</h3>
        <div className="flex items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Date
            </label>
            <input
              type="date"
              name="fromDate"
              value={dateRange.fromDate}
              onChange={handleDateChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Date
            </label>
            <input
              type="date"
              name="toDate"
              value={dateRange.toDate}
              onChange={handleDateChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <button
            onClick={handleApplyFilter}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Apply Filter
          </button>
          <button
            onClick={() => {
              setDateRange({ fromDate: "", toDate: "" });
              setTimeout(loadAnalytics, 0);
            }}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border rounded-lg p-6">
          <p className="text-sm text-gray-600 mb-1">Total Orders</p>
          <p className="text-3xl font-bold text-blue-600">
            {overview.totalInstallmentOrders || 0}
          </p>
        </div>

        <div className="bg-white border rounded-lg p-6">
          <p className="text-sm text-gray-600 mb-1">Total Financed</p>
          <p className="text-3xl font-bold text-green-600">
            ₹{(overview.totalFinanced || 0).toLocaleString()}
          </p>
        </div>

        <div className="bg-white border rounded-lg p-6">
          <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
          <p className="text-3xl font-bold text-purple-600">
            ₹{totalRevenue.toLocaleString()}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Interest + Processing Fees
          </p>
        </div>

        <div className="bg-white border rounded-lg p-6">
          <p className="text-sm text-gray-600 mb-1">Avg EMI Amount</p>
          <p className="text-3xl font-bold text-gray-900">
            ₹{Math.round(overview.avgEMIAmount || 0).toLocaleString()}
          </p>
        </div>

        <div className="bg-white border rounded-lg p-6">
          <p className="text-sm text-gray-600 mb-1">Interest Earned</p>
          <p className="text-2xl font-bold text-indigo-600">
            ₹{(overview.totalInterestEarned || 0).toLocaleString()}
          </p>
        </div>

        <div className="bg-white border rounded-lg p-6">
          <p className="text-sm text-gray-600 mb-1">Processing Fees</p>
          <p className="text-2xl font-bold text-cyan-600">
            ₹{(overview.totalProcessingFees || 0).toLocaleString()}
          </p>
        </div>

        <div className="bg-white border rounded-lg p-6">
          <p className="text-sm text-gray-600 mb-1">Avg Tenure</p>
          <p className="text-2xl font-bold text-gray-900">
            {Math.round(overview.avgTenure || 0)} months
          </p>
        </div>

        <div className="bg-white border rounded-lg p-6">
          <p className="text-sm text-gray-600 mb-1">Default Rate</p>
          <p className="text-2xl font-bold text-red-600">
            {defaultRate.toFixed(2)}%
          </p>
        </div>
      </div>

      {/* Payment Performance */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Payment Performance</h3>
        <div className="grid grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-2">Total EMIs</p>
            <p className="text-3xl font-bold">
              {paymentPerformance.totalEmis || 0}
            </p>
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-2">Paid EMIs</p>
            <div className="flex items-center gap-2">
              <p className="text-3xl font-bold text-green-600">
                {paymentPerformance.totalPaidEmis || 0}
              </p>
              <div className="flex items-center text-green-600">
                <TrendingUpIcon className="h-5 w-5" />
                <span className="text-sm font-semibold">
                  {overview.onTimePaymentRate || 0}%
                </span>
              </div>
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-2">Overdue EMIs</p>
            <div className="flex items-center gap-2">
              <p className="text-3xl font-bold text-red-600">
                {paymentPerformance.totalOverdueEmis || 0}
              </p>
              <div className="flex items-center text-red-600">
                <TrendingDownIcon className="h-5 w-5" />
                <span className="text-sm font-semibold">
                  {overview.overduePaymentRate || 0}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance by Status */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Performance by Status</h3>
        <div className="space-y-3">
          {byStatus.map((status) => (
            <div
              key={status._id}
              className="flex justify-between items-center p-4 bg-gray-50 rounded-lg"
            >
              <div>
                <p className="font-semibold">{status._id || "Unknown"}</p>
                <p className="text-sm text-gray-600">{status.count} orders</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold">
                  ₹{(status.totalValue || 0).toLocaleString()}
                </p>
              </div>
            </div>
          ))}

          {byStatus.length === 0 && (
            <p className="text-center text-gray-500 py-4">
              No status data available
            </p>
          )}
        </div>
      </div>

      {/* Performance by Plan */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Performance by Plan</h3>
        <div className="space-y-3">
          {byPlan.map((plan) => (
            <div
              key={plan._id}
              className="flex justify-between items-center p-4 bg-gray-50 rounded-lg"
            >
              <div>
                <p className="font-semibold">{plan._id || "Unknown Plan"}</p>
                <p className="text-sm text-gray-600">{plan.count} orders</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold">
                  ₹{(plan.totalFinanced || 0).toLocaleString()}
                </p>
                <p className="text-sm text-gray-600">financed</p>
              </div>
            </div>
          ))}

          {byPlan.length === 0 && (
            <p className="text-center text-gray-500 py-4">
              No plan data available
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallmentAnalytics;
